
import React, { useState, useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";

import { 
  Campaign,
} from "@dndModels";
import { CAMPAIGN_OBJECT_TYPES } from "@dndModels/DataTypeConstants";

import API from "@dndUtilities/system_api";
import { makeStyles } from "@utilities/theme_utilities";

import DisplayObject from "@dndComponents/display/DisplayObject";
import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";
import FlexBox from "@components/layout/FlexBox";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import CommonUtilities from "@utilities/common_utilities";
import ToastBox from "@components/display/ToastBox";
import CopyButton from "@components/input/buttons/icon_buttons/CopyButton";
import ArchiveButton from "@components/input/buttons/icon_buttons/ArchiveButton";

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

interface Props {
  campaign: Campaign;
  onUpdated: () => void;
}

export default function CampaignInput({ campaign, onUpdated }: Props) {
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [ping, setPing] = useState(false);
  const [toast_time, setToastTime] = useState(-1);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const updateCampaign = async (updates?: Partial<Campaign>) => {
    const user = api.user;
    // debugger;
    if (user && updates) {
      setProcessing(true);
      Object.assign(campaign, updates);
      await api.upsertObject("campaign", campaign);
      setProcessing(false);
    }
  };

  const copyCampaign = async () => {
    const user = api.user;
    if (user) {
      setProcessing(true);
      // There are lots of pieces to a campaign, like a character, 
      // but when copying a campaign, we only want to copy the base campaign, and the char options.
      
      const user = api.user;
      if (user) {
        const from_db = await api.getCampaign({ campaign_id: campaign._id });
        if (from_db && from_db.char_options) {
          campaign.copy(from_db);
          const new_obj = campaign.baseClone();
          new_obj.name = new_obj.name + " (Copy)";
          new_obj._id = "";
          const res = await api.createObject("campaign", new_obj);
          if (res) {
            // Now the char options.
            const new_char_options = from_db.char_options.clone();
            new_char_options.campaign_id = new_obj._id;
            const res2 = await api.createObject("campaign_char_options", new_char_options);
            if (res2) {
              new_char_options.campaign = new_obj;
              new_obj.char_options = new_char_options;
              setNavigateTo(`/campaign/${new_obj._id}`);
            }
          }
        }
      }
      setProcessing(false);
    }
  }

  const deleteCampaign = async () => {
    const user = api.user;
    if (user) {
      setProcessing(true);
      for (const data_type of CAMPAIGN_OBJECT_TYPES) {
        const c_data = await api.getCampaignObjects({ data_type, filter: { campaign_id: campaign._id } });
        for (const c_obj of c_data) {
          await api.deleteObject(data_type, c_obj);
        }
      }
      await api.deleteObject("campaign", campaign);
      setProcessing(false);
    }
  }
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        marginBottom: '4px',
      },
    });
  });

  const { classes, cx } = useStyles();

  // const renderOpenButton = () => {
  //   return (
  //     <>
  //       <FlexBox className="centered">
  //         <DandDButtonBox
  //           name="Open" 
  //           disabled={ processing } 
  //           link_url={`/campaign/${campaign._id}`}
  //           onClick={() => {
  //             setNavigateTo(`/campaign/${campaign._id}`);
  //           }}
  //         />
  //       </FlexBox>
  //     </>
  //   );
  // } 

  const renderJoinButton = () => {
    return (
      <>
        <FlexBox className="centered">
          <DandDButtonBox
            name="Join Link"
            disabled={processing}
            onClick={ async () => {
              navigator.clipboard.writeText(`${window.location.origin}/campaign/join/${campaign._id}/${campaign.reusable_invite}`);
              setToastTime(common_util.get_time_number());
            }}
          />
        </FlexBox>
      </>
    );
  } 

  const renderCopyButton = () => {
    return (
      <>
        <FlexBox className="centered">
          <CopyButton
            no_fab
            // log_it
            disabled={ processing || !api.online } 
            onClick={copyCampaign}
          />
        </FlexBox>
      </>
    );
  } 

  const renderArchiveButton = () => {
    return (
      <>
        <FlexBox className="centered">
          <ArchiveButton
            no_fab
            // log_it
            is_archived={campaign.is_archived}
            disabled={ processing || !api.online } 
            onClick={ async () => {
              await updateCampaign({ is_archived: !campaign.is_archived });
              onUpdated();
              setPing(!ping);
            }}
          />
        </FlexBox>
      </>
    );
  } 

  const renderDeleteButton = () => {
    return (
      <>
        <FlexBox className="centered">
          <DeleteButton
            no_fab
            obj_name={campaign.name}
            disabled={ processing || !api.online } 
            onClick={deleteCampaign}
          />
        </FlexBox>
      </>
    );
  } 

  const renderControls = () => {
    return (
      <FlexBox direction="column">
        <FlexBox direction="row">
          {/* { renderOpenButton() } */}
          { renderJoinButton() }
          { renderCopyButton() }
          { renderArchiveButton() }
          { renderDeleteButton() }
        </FlexBox>
      </FlexBox>
    );
  }

  const renderMainDetails = () => {
    return (
      <FlexBox direction="row">
        <FlexBox className="padded">
          <DisplayObject obj={campaign}
            disabled={ processing } 
            link_url={`/campaign/${campaign._id}`}
            // log_it
            onClick={() => {
              setNavigateTo(`/campaign/${campaign._id}`);
            }} 
          />
        </FlexBox>
        { campaign.image_url !== "" &&
          <FlexBox className="padded">
            <div style={{ float: "right" }}>
              <img src={ campaign.image_url } alt="character image" 
                style={{
                  maxWidth: "35px",
                  maxHeight: "35px",
                }}
              />
            </div>
          </FlexBox>
        }
      </FlexBox>
    );
  }

  if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else {
    return (
      <div className="padded">
        <FlexBox direction="column" className={cx(classes.root)}>
          { renderMainDetails() }
          { renderControls() }
        </FlexBox>
        <ToastBox
          openTime={ toast_time }
        >
          Join Link copied to Clipboard
        </ToastBox>
      </div>
    );
  }
}
